import React, { FC, Fragment, ChangeEventHandler, FocusEventHandler } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useQuery } from '@apollo/client';
import { getParentClinicsTemplate } from 'apollo/templates/user/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { ClinicDropdown } from 'models/user';
import { useTranslation } from 'react-i18next';

type ClinicsAutocompleteProps = {
    onParentClinicChange: (clinic?: ClinicDropdown | null) => void;
    value?: ClinicDropdown | null;
    name?: string;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    onBlur?: FocusEventHandler<HTMLInputElement>;
    helperText?: string | boolean;
    error?: boolean;
    fullWidth?: boolean;
    useInForm?: boolean;
    required?: boolean;
};
const ClinicsAutocomplete: FC<ClinicsAutocompleteProps> = ({
    onParentClinicChange,
    value,
    name,
    onChange,
    onBlur,
    helperText,
    error,
    fullWidth,
    useInForm,
    required,
}: ClinicsAutocompleteProps) => {
    const { t: translate } = useTranslation('users');
    const { showBoundary } = useDenthubErrorBoundary();
    const { data, loading: clinicsLoading } = useQuery(getParentClinicsTemplate, {
        notifyOnNetworkStatusChange: true,
        onError(error) {
            showBoundary(error);
        },
    });

    return (
        <Fragment>
            <Autocomplete
                fullWidth={fullWidth}
                id="select-parent-clinic"
                value={value}
                options={data ? data.parentClinics : []}
                getOptionLabel={(option: ClinicDropdown) => option.name || ''}
                isOptionEqualToValue={(option: ClinicDropdown, value: ClinicDropdown) => option?._id === value?._id}
                renderOption={(props, option: ClinicDropdown) => (
                    <li {...{ ...props, key: option?._id }}>{option?.name || ''}</li>
                )}
                onChange={(_event, value) => {
                    onParentClinicChange(value);
                }}
                disabled={clinicsLoading}
                renderInput={(params) => (
                    <TextField
                        sx={{ minWidth: useInForm ? 0 : 300 }}
                        {...params}
                        label={translate('users:SELECT_CLINIC')}
                        margin="dense"
                        required={required ? true : false}
                        value={value}
                        name={name}
                        onChange={onChange}
                        onBlur={onBlur}
                        helperText={helperText}
                        error={error}
                    />
                )}
            />
        </Fragment>
    );
};

export default ClinicsAutocomplete;
